import type {
  ClassSchedule,
  CreateClassScheduleRequest,
  ScheduleException,
  UpdateClassScheduleRequest,
} from '../../api/class-schedule.service';
import type { DemoStoreData } from '../store-types';
import { route, type HandlerEntry } from '../handler-registry';

function newScheduleId(data: DemoStoreData): string {
  return `sched-${data.schedules.length + 1}-${Date.now().toString(36).slice(-4)}`;
}

export const schedulesHandlers: readonly HandlerEntry[] = [
  route('GET', /^\/api\/tenants\/([^/]+)\/class-schedules$/, (_ctx, data) => data.schedules),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/class-schedules\/([^/]+)$/,
    (ctx, data) => {
      const id = decodeURIComponent(ctx.match[2]);
      const found = data.schedules.find((s) => s.scheduleId === id);
      return found ?? { error: { status: 404, message: 'Not found' } };
    },
  ),
  route('POST', /^\/api\/tenants\/([^/]+)\/class-schedules$/, (ctx) => {
    const body = ctx.body as CreateClassScheduleRequest;
    return {
      mutate: (data: DemoStoreData) => {
        const schedule: ClassSchedule = {
          scheduleId: newScheduleId(data),
          tenantId: data.tenant.tenantId,
          groupId: body.groupId ?? null,
          locationId: body.locationId,
          coachTenantAccountIds: body.coachTenantAccountIds ?? [],
          dayOfWeek: body.dayOfWeek,
          startTime: body.startTime,
          endTime: body.endTime,
          effectiveFrom: body.effectiveFrom,
          effectiveUntil: body.effectiveUntil ?? null,
          title: body.title ?? null,
          notes: body.notes ?? null,
          status: 'Active',
          createdAt: new Date().toISOString(),
          updatedAt: null,
        };
        return {
          next: { ...data, schedules: [...data.schedules, schedule] },
          result: schedule,
        };
      },
    };
  }),
  route(
    'PATCH',
    /^\/api\/tenants\/([^/]+)\/class-schedules\/([^/]+)$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as UpdateClassScheduleRequest;
      return {
        mutate: (data: DemoStoreData) => {
          const schedules = data.schedules.map((s) =>
            s.scheduleId === id
              ? {
                  ...s,
                  groupId: body.groupId === undefined ? s.groupId : body.groupId,
                  locationId: body.locationId ?? s.locationId,
                  coachTenantAccountIds:
                    body.coachTenantAccountIds ?? s.coachTenantAccountIds,
                  dayOfWeek: body.dayOfWeek ?? s.dayOfWeek,
                  startTime: body.startTime ?? s.startTime,
                  endTime: body.endTime ?? s.endTime,
                  effectiveFrom: body.effectiveFrom ?? s.effectiveFrom,
                  effectiveUntil:
                    body.effectiveUntil === undefined ? s.effectiveUntil : body.effectiveUntil,
                  title: body.title === undefined ? s.title : body.title,
                  notes: body.notes === undefined ? s.notes : body.notes,
                  status: body.status ?? s.status,
                  updatedAt: new Date().toISOString(),
                }
              : s,
          );
          const result = schedules.find((s) => s.scheduleId === id)!;
          return { next: { ...data, schedules }, result };
        },
      };
    },
  ),
  route(
    'DELETE',
    /^\/api\/tenants\/([^/]+)\/class-schedules\/([^/]+)$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      return {
        mutate: (data: DemoStoreData) => ({
          next: {
            ...data,
            schedules: data.schedules.filter((s) => s.scheduleId !== id),
          },
          result: undefined as void,
        }),
      };
    },
  ),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/class-schedules\/([^/]+)\/exceptions$/,
    (): readonly ScheduleException[] => [],
  ),
];
